import { Fragment, useEffect, useRef, useState } from 'react';
import type { AuditEvent } from '@reef/schema';
import { api, type WorkflowSummary } from '../api.ts';
import { IdentityBadge, identityKindFor } from '../components/IdentityBadge.tsx';
import { useSidebarPane } from '../components/PaneResizer.tsx';

/**
 * The admin log.
 *
 * The columns are chosen around one question a reader keeps asking: *whose
 * access was this?* An action attributed to a GitHub App is a very different
 * fact from the same action attributed to a person, so identity gets its own
 * column rather than being buried in the detail blob.
 */
export function AuditView({ workflows }: { workflows: WorkflowSummary[] }) {
  const [workflowId, setWorkflowId] = useState<string>(workflows[0]?.id ?? '');
  const [type, setType] = useState<string>('');
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  /*
   * Which request the table is allowed to believe.
   *
   * Two filters, one effect, and no ordering guarantee between the responses:
   * changing the workflow and then the event type starts two queries, and the
   * first is broader and therefore usually slower. Without this, the broader
   * answer lands last and the table shows rows that do not match the filters
   * sitting above them — which reads as "the filter does not work" rather than
   * as a race, and is the kind of thing somebody reports as a missing row in an
   * audit log.
   */
  const latest = useRef(0);

  const load = () => {
    const mine = (latest.current += 1);
    setLoading(true);
    setError(null);
    api
      .audit({ workflowId: workflowId || undefined, type: type || undefined, limit: '200' })
      .then((result) => {
        if (mine === latest.current) setEvents(result.events);
      })
      .catch((caught) => {
        if (mine === latest.current) setError((caught as Error).message);
      })
      .finally(() => {
        if (mine === latest.current) setLoading(false);
      });
  };

  useEffect(load, [workflowId, type]);

  const pane = useSidebarPane('reef.audit.sidebar', 260);

  return (
    <div {...pane.body}>
      <aside className="sidebar">
        <p className="section-title">Filters</p>
        <div className="stack">
          <div className="field">
            <label htmlFor="audit-workflow">Workflow</label>
            <select
              id="audit-workflow"
              value={workflowId}
              onChange={(event) => setWorkflowId(event.target.value)}
            >
              <option value="">All (admins only)</option>
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="audit-type">Event type</label>
            <select id="audit-type" value={type} onChange={(event) => setType(event.target.value)}>
              <option value="">Everything</option>
              {EVENT_TYPES.map((entry) => (
                <option key={entry} value={entry}>
                  {entry}
                </option>
              ))}
            </select>
          </div>

          <button onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </aside>

      {pane.resizer}

      <main className="main">
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Audit</h1>
        <p className="hint" style={{ marginTop: 0 }}>
          Every step input, tool call, policy decision, approval, and the identity each ran under.
          Retained permanently; large payloads live in R2 behind the payload link.
        </p>

        {error && (
          <div className="banner danger">
            <h3>Could not load the audit log</h3>
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Step</th>
                <th>Identity</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                /*
                  A keyed `Fragment`, not `<>`. A row expands into a second `tr`
                  beneath it, so one event is two children and they have to be
                  wrapped — but the wrapper is what `tbody` sees in the list, so
                  keying the `tr`s inside it keys nothing. React warns, and the
                  warning is not cosmetic: reordering or filtering the log then
                  reconciles rows against the wrong previous row, which is how
                  an expanded detail pane ends up open under a different event.
                  Only reachable with rows on screen, which is why an empty
                  audit table never showed it.
                */
                <Fragment key={event.id}>
                  <tr onClick={() => setExpanded(expanded === event.id ? null : event.id)}>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--text-faint)' }}>
                      {new Date(event.at).toLocaleTimeString()}
                    </td>
                    <td>
                      <span className={`pill ${tone(event.type)}`}>{event.type}</span>
                    </td>
                    <td>
                      <code>{event.stepId ?? '—'}</code>
                    </td>
                    <td>{renderIdentity(event)}</td>
                    <td>{event.summary ?? '—'}</td>
                  </tr>
                  {expanded === event.id && (
                    <tr>
                      <td colSpan={5} style={{ background: 'var(--surface-sunken)' }}>
                        <pre>{JSON.stringify(event.detail ?? {}, null, 2)}</pre>
                        {event.payloadRef && (
                          <p className="hint" style={{ marginBottom: 0 }}>
                            Full payload:{' '}
                            <a href={`/api/v1/audit/payload?key=${encodeURIComponent(event.payloadRef)}`}>
                              {event.payloadRef}
                            </a>
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {events.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="hint">
                    Nothing matches these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

const EVENT_TYPES = [
  'step.started',
  'step.finished',
  'tool.call',
  'policy.decision',
  'approval.requested',
  'approval.granted',
  'approval.rejected',
  'connection.denied',
  'runtime.escalated',
  'run.started',
  'run.forked',
  // The row somebody goes looking for having found a run stopped half-way with
  // nobody's name on it. Without it in this list the only way to that answer is
  // an unfiltered scroll through everything the run did.
  'run.paused',
  'workflow.updated',
];

/**
 * Whose credential actually performed the action.
 *
 * This column used to print `user:maQfAOEr… (linear)`, which is the right data
 * and the wrong shape — the question a reader brings to an audit log is "was
 * this a person or the bot", and that was the hardest thing to see. Same badge
 * as the builder and the Connections screen, so one glance down the column
 * separates human actions from automated ones.
 */
function renderIdentity(event: AuditEvent) {
  if (!event.identity) {
    return event.actorUserId ? (
      // The address when reef still knows one, the id when it does not — an
      // actor who has been removed is a fact worth showing rather than
      // rendering as a blank cell. The id stays in the tooltip either way,
      // because that is what the API filters on.
      <IdentityBadge
        kind="person"
        label={event.actorEmail ?? event.actorUserId}
        tip={event.actorEmail ? `Reef user ${event.actorUserId}.` : 'No account with this id any more.'}
      />
    ) : (
      <IdentityBadge kind="bot" label="system" tip="Reef's own control plane, not a step." />
    );
  }

  const { kind, id, connectionId, display } = event.identity;
  const badgeKind = identityKindFor(kind);
  const via = connectionId ? ` via ${connectionId}` : '';

  return (
    <IdentityBadge
      kind={badgeKind}
      label={display ?? id}
      tip={
        badgeKind === 'person'
          ? `A person's own credential${via}. This action carried their name and their permissions.`
          : `A shared workspace credential${via}. No individual's access was used, and no individual is accountable for it.`
      }
    />
  );
}

function tone(type: string): string {
  if (type.startsWith('policy') || type.includes('denied')) return 'warn';
  if (type.includes('rejected') || type.includes('failed')) return 'danger';
  if (type.includes('granted') || type.includes('finished')) return 'ok';
  return '';
}
